import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

export const GoogleCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');
    const error = searchParams.get('error');

    if (error || !token) {
      navigate('/login?error=' + (error || 'unknown'), { replace: true });
      return;
    }

    loginWithToken(token).then(() => {
      navigate('/dashboard', { replace: true });
    }).catch(() => {
      navigate('/login?error=token_invalid', { replace: true });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#101828',
      fontFamily: 'inherit',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background glow blobs */}
      <div style={{
        position: 'absolute',
        width: 500,
        height: 500,
        borderRadius: '50%',
        background: 'rgba(68,76,231,0.18)',
        top: -180,
        right: -180,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        width: 350,
        height: 350,
        borderRadius: '50%',
        background: 'rgba(97,114,243,0.14)',
        bottom: -100,
        left: -80,
        pointerEvents: 'none',
      }} />

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.5rem',
        zIndex: 1,
      }}>
        {/* Logo */}
        <span style={{
          fontSize: '2rem',
          fontWeight: 800,
          color: 'var(--ds-text-on-dark)',
          letterSpacing: '-0.03em',
          lineHeight: 1,
        }}>
          Policyora
        </span>

        {/* Spinner */}
        <div style={{ position: 'relative', width: 52, height: 52 }}>
          <svg
            width="52"
            height="52"
            viewBox="0 0 52 52"
            style={{ animation: 'spin 0.9s linear infinite' }}
          >
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <circle
              cx="26" cy="26" r="22"
              fill="none"
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="4"
            />
            <circle
              cx="26" cy="26" r="22"
              fill="none"
              stroke="url(#grad)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="138"
              strokeDashoffset="100"
            />
            <defs>
              <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="var(--ds-brand-blue-light)" />
                <stop offset="100%" stopColor="var(--primary-200)" />
              </linearGradient>
            </defs>
          </svg>

          {/* Google G in the centre */}
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="var(--ds-brand-blue-light)" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="var(--ds-success)" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="var(--ds-brand-blue)" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="var(--ds-danger)" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
        </div>

        {/* Label */}
        <p style={{
          color: 'rgba(255,255,255,0.75)',
          fontSize: '0.95rem',
          fontWeight: 500,
          margin: 0,
          letterSpacing: '0.01em',
        }}>
          Signing you in with Google…
        </p>
      </div>
    </div>
  );
};
